import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CATS } from "../data/constants";
import { useAuth } from "../lib/auth.jsx";
import { Button } from "./Button";
import { bookingDisplayCode } from "../lib/bookingCode.js";
import { NailPlanViewer } from "./NailPlanViewer.jsx";
import { bookingDesign, designImageUrl } from "../lib/designMedia.js";

const CURRENCY_SYMBOLS = {
  USD: "$", CAD: "CA$", GBP: "£", EUR: "€", NGN: "₦",
  GHS: "GH₵", ZAR: "R", KES: "KSh", AUD: "A$", NZD: "NZ$",
  AED: "د.إ", INR: "₹", SGD: "S$", MYR: "RM", JPY: "¥",
  BRL: "R$", MXN: "MX$", EGP: "E£", SAR: "﷼", ILS: "₪",
  CNY: "¥", KRW: "₩", THB: "฿", HKD: "HK$", IDR: "Rp",
};

function fmtCurrency(cents, currencyCode = "USD") {
  const code = String(currencyCode || "USD").toUpperCase();
  const amount = (Number(cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${CURRENCY_SYMBOLS[code] || code}${amount}`;
}

/**
 * Generic full-screen modal with a click-out backdrop.
 * Exported so other screens (HistoryScreen, AdminDashboard) share one
 * implementation rather than each declaring their own.
 */
export function Modal({ title, children, onClose, maxWidth = 540 }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-elevated)", color: "var(--text)",
          borderRadius: 16, padding: 22, width: "100%", maxWidth, maxHeight: "85vh", overflowY: "auto",
          border: "1px solid var(--border)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ fontFamily: "var(--font-display)", fontSize: 18 }}>{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--text-muted)" }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Detail view for a single design request row, used by both the customer's
 * /history view and the admin console. Pass the full request object —
 * `category`, `created_at`, `final_design`, `generated[]`, `status`, and
 * (optionally) `customer { full_name, email, phone }`.
 *
 * Only the fields present render; the modal degrades gracefully when a
 * request never finalised a pick or is missing optional metadata.
 */
export function DesignDetailModal({ req, onClose, showCustomer = false }) {
  const { profile, authedFetch } = useAuth();
  const nav = useNavigate();
  const [confirmingDesign, setConfirmingDesign] = useState(null);
  const [currencyCode, setCurrencyCode] = useState("USD");

  useEffect(() => {
    let alive = true;
    authedFetch("/api/settings/currency")
      .then((s) => {
        if (alive && s?.currency) setCurrencyCode(String(s.currency).toUpperCase());
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // The supplies/products list is technician operational info — kit each
  // tech needs to do the design — and confuses customers. Only staff see it.
  const isStaff = ["technician", "admin_tech"].includes(profile?.role);
  // The owner of the request and any staff member can re-pick a previously
  // generated design for a new appointment without burning a fresh
  // generation cycle.
  const isOwner = profile?.id && profile.id === req.customer_id;
  const canReuse = isStaff || isOwner;

  function initiateReuse(design) {
    setConfirmingDesign(design);
  }

  function confirmReuse() {
    if (!confirmingDesign) return;
    // Stash the chosen design + request id in sessionStorage so the
    // booking flow can pick it up at step 4 (slot picker), skipping
    // categories + AI generation entirely.
    try {
      sessionStorage.setItem("oge.reuse", JSON.stringify({
        requestId: req.id,
        design: confirmingDesign,
      }));
    } catch { /* private mode etc — fall through to query string */ }
    onClose?.();
    nav(`/book?reuse=${encodeURIComponent(req.id)}`);
  }

  const cat = CATS.find((c) => c.id === req.category);
  const finalised = req.final_design;
  const generated = Array.isArray(req.generated) ? req.generated : [];
  const designs = finalised
    ? [finalised, ...generated.filter((g) => g?.id !== finalised.id)]
    : generated;

  return (
    <Modal title={finalised?.name || "Design request"} onClose={onClose} maxWidth={680}>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
        {cat?.icon} {cat?.label || req.category} ·{" "}
        {new Date(req.created_at).toLocaleString()}
        {req.status && (
          <>
            {" "}· status: <b style={{ color: "var(--text)" }}>{req.status}</b>
          </>
        )}
      </div>

      {showCustomer && req.customer && (
        <section
          style={{
            background: "var(--bg-soft)",
            borderRadius: 12,
            padding: "10px 12px",
            marginBottom: 14,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 8,
          }}
        >
          <Meta label="Customer">{req.customer.full_name || "—"}</Meta>
          {req.customer.email && <Meta label="Email">{req.customer.email}</Meta>}
          {req.customer.phone && <Meta label="Phone">{req.customer.phone}</Meta>}
        </section>
      )}

      {designs.length === 0 ? (
        <div style={{ color: "var(--text-muted)" }}>This request didn't produce any designs.</div>
      ) : (
        <div style={{ display: "grid", gap: 14 }}>
          {designs.map((d, i) => (
            <DesignBlock
              key={d.id || i}
              design={d}
              highlight={finalised && d.id === finalised.id}
              isStaff={isStaff}
              canReuse={canReuse}
              onReuse={canReuse ? () => initiateReuse(d) : undefined}
            />
          ))}
        </div>
      )}

      {confirmingDesign && (
        <ReuseConfirmationModal
          design={confirmingDesign}
          onConfirm={confirmReuse}
          onPreview={() => setConfirmingDesign(null)}
          onCancel={() => setConfirmingDesign(null)}
        />
      )}
    </Modal>
  );
}

export function BookingDetailModal({
  booking,
  title = "Appointment details",
  onClose,
  onIcs,
  onWallet,
  onReschedule,
  onCancel,
  onReview,
  onMarkPaid,
  onRebook,
  calendarAdded = false,
  walletAdded = false,
}) {
  if (!booking) return null;
  const design = bookingDesign(booking);
  const imageUrl = designImageUrl(design);
  const startsAt = booking.slot?.starts_at ? new Date(booking.slot.starts_at) : null;
  const completed = ["completed", "complete"].includes(String(booking.status).toLowerCase());
  const inFuture = startsAt && startsAt.getTime() > Date.now();
  const canReschedule = inFuture && ["pending", "confirmed", "rescheduled"].includes(booking.status);
  const total = Number(booking.price_cents || 0) + Number(booking.tip_cents || 0);
  const invoice = booking.invoice || booking.invoices?.[0] || null;
  const invoiceStatus = String(invoice?.status || "").toLowerCase();
  const finalPaymentClosed = ["paid", "void", "voided", "failed", "refunded"].includes(invoiceStatus);

  return (
    <Modal title={title} onClose={onClose} maxWidth={760}>
      <div style={{ display: "grid", gap: 16 }}>
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            style={{ width: "100%", maxHeight: 360, objectFit: "cover", borderRadius: 16, border: "1px solid var(--border)" }}
          />
        )}
        <section style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 12, color: "var(--text-accent)", fontWeight: 800, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
              Booking #{bookingDisplayCode(booking.id)}
            </div>
            <h2 style={{ margin: "4px 0 0", color: "var(--text)", fontFamily: "var(--font-display)", fontSize: 28 }}>
              {design.name || "Appointment"}
            </h2>
            <p style={{ margin: "6px 0 0", color: "var(--text-muted)", fontSize: 14 }}>
              {design.tagline || booking.request?.category || "Oge appointment"}
            </p>
          </div>
          <span style={{
            alignSelf: "flex-start",
            fontSize: 12,
            fontWeight: 800,
            padding: "6px 14px",
            borderRadius: 999,
            background: "rgba(196,135,154,0.16)",
            color: "var(--text-accent)",
          }}>
            {formatBookingStatus(booking.status)}
          </span>
        </section>

        <section style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10,
        }}>
          <Meta label="Appointment">{startsAt ? startsAt.toLocaleString() : "Slot pending"}</Meta>
          <Meta label="Booked">{booking.created_at ? new Date(booking.created_at).toLocaleString() : "Unknown"}</Meta>
          {completed && <Meta label="Completed">{booking.updated_at ? new Date(booking.updated_at).toLocaleString() : "Unknown"}</Meta>}
          <Meta label="Technician">{booking.technician?.full_name || booking.technician?.email || "Unassigned"}</Meta>
          <Meta label="Customer">{booking.customer?.full_name || booking.customer?.email || "Customer"}</Meta>
          <Meta label="Total">{fmtCurrency(total, invoice?.currency || booking.currency)}</Meta>
          {invoice && <Meta label="Invoice">{invoice.number || invoice.id?.slice(0, 8)} · {formatBookingStatus(invoice.status)}</Meta>}
        </section>

        {invoice && (
          <div style={{
            borderRadius: 14,
            padding: "12px 14px",
            border: `1px solid ${invoiceStatus === "paid" ? "rgba(50,160,80,0.30)" : "var(--border)"}`,
            background: invoiceStatus === "paid" ? "rgba(50,160,80,0.12)" : "var(--bg-soft)",
            color: invoiceStatus === "paid" ? "var(--text-confirm)" : "var(--text)",
            fontWeight: 800,
          }}>
            {invoiceStatus === "paid" ? "Paid" : `Invoice ${formatBookingStatus(invoice.status)}`}
            {invoiceStatus === "paid" && invoice.paid_at ? ` · ${new Date(invoice.paid_at).toLocaleString()}` : ""}
          </div>
        )}

        {design.description && (
          <p style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.7, margin: 0 }}>
            {design.description}
          </p>
        )}

        {Array.isArray(design.products) && design.products.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {design.products.map((p, i) => (
              <span key={i} style={{
                fontSize: 12,
                padding: "5px 10px",
                borderRadius: 999,
                background: "rgba(196,135,154,0.14)",
                color: "var(--text-accent)",
                border: "1px solid var(--border-strong)",
                fontWeight: 700,
              }}>
                {p}
              </span>
            ))}
          </div>
        )}

        <NailPlanViewer design={design} workProgress={booking.work_progress || {}} mode="preview" />

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {onReschedule && canReschedule && <Button size="sm" variant="outline" label="Reschedule" onClick={onReschedule} />}
          {onCancel && !completed && booking.status !== "cancelled" && <Button size="sm" variant="danger" label="Cancel" onClick={onCancel} />}
          {onIcs && <Button size="sm" variant="ghost" label={calendarAdded ? "Calendar added" : "Add to Calendar"} onClick={onIcs} disabled={completed || calendarAdded || !startsAt} />}
          {onWallet && <Button size="sm" variant="outline" label={walletAdded ? "Wallet added" : "Add to Wallet"} onClick={onWallet} disabled={completed || walletAdded} />}
          {onReview && completed && !booking.review && <Button size="sm" variant="gold" label="Leave review" onClick={onReview} />}
          {onMarkPaid && completed && !finalPaymentClosed && <Button size="sm" variant="primary" label="Mark Final Payment Paid" onClick={onMarkPaid} />}
          {onRebook && completed && imageUrl && <Button size="sm" variant="primary" label="Rebook →" onClick={onRebook} />}
        </div>
      </div>
    </Modal>
  );
}

function DesignBlock({ design: d, highlight, isStaff, canReuse, onReuse }) {
  const imageUrl = designImageUrl(d);
  return (
    <section
      style={{
        borderRadius: 14,
        padding: 14,
        border: highlight ? "1.5px solid var(--accent)" : "1px solid var(--border)",
        background: highlight ? "rgba(196,135,154,0.08)" : "var(--bg-elevated)",
        display: "grid",
        gridTemplateColumns: imageUrl ? "120px 1fr" : "1fr",
        gap: 14,
        alignItems: "flex-start",
      }}
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt={d.name || "Design"}
          style={{ width: 120, height: 120, borderRadius: 10, objectFit: "cover" }}
        />
      )}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
            {d.name || "Untitled design"}
          </div>
          {highlight && (
            <span
              style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase",
                padding: "3px 8px", borderRadius: 999, background: "var(--accent)", color: "#fff",
              }}
            >
              Booked
            </span>
          )}
        </div>
        {d.tagline && (
          <div style={{ fontSize: 12, fontStyle: "italic", color: "var(--accent)", marginTop: 2 }}>
            {d.tagline}
          </div>
        )}
        {d.description && (
          <p style={{ fontSize: 13, color: "var(--text)", marginTop: 8, lineHeight: 1.55 }}>
            {d.description}
          </p>
        )}

        {Array.isArray(d.colors) && d.colors.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <Meta label="Colours">
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {d.colors.map((hex, i) => (
                  <span
                    key={i}
                    title={hex}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "3px 8px 3px 6px", borderRadius: 999,
                      background: "var(--bg-soft)", color: "var(--text)", fontSize: 11, fontWeight: 600,
                    }}
                  >
                    <span style={{ width: 12, height: 12, borderRadius: "50%", background: hex, border: "1px solid var(--border)" }} />
                    {hex}
                  </span>
                ))}
              </div>
            </Meta>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, marginTop: 10 }}>
          {d.nailShape  && <Meta label="Shape">{d.nailShape}</Meta>}
          {d.nailLength && <Meta label="Length">{d.nailLength}</Meta>}
          {d.finish     && <Meta label="Finish">{d.finish}</Meta>}
          {d.technique  && <Meta label="Technique">{d.technique}</Meta>}
          {d.duration   && <Meta label="Duration">{d.duration}</Meta>}
          {d.priceRange && <Meta label="Price">{d.priceRange}</Meta>}
          {d.difficulty && <Meta label="Difficulty">{d.difficulty}</Meta>}
          {d.mood       && <Meta label="Mood">{d.mood}</Meta>}
        </div>

        {isStaff && Array.isArray(d.products) && d.products.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <Meta label="Supplies needed (staff only)">
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {d.products.map((p, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: 11, padding: "3px 9px", borderRadius: 999,
                      background: "rgba(196,135,154,0.14)",
                      color: "var(--text-accent, var(--accent))",
                      border: "1px solid var(--border-strong)", fontWeight: 600,
                    }}
                  >
                    ✓ {p}
                  </span>
                ))}
              </div>
            </Meta>
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <NailPlanViewer design={d} mode="preview" />
        </div>

        {canReuse && onReuse && (
          <div style={{
            marginTop: 14, paddingTop: 14,
            borderTop: "1px dashed var(--border)",
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          }}>
            <button
              type="button"
              onClick={onReuse}
              style={{
                background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                color: "#fff",
                fontSize: 13, fontWeight: 700,
                padding: "10px 16px",
                borderRadius: 999,
                border: "none", cursor: "pointer",
                boxShadow: "0 6px 18px rgba(196, 135, 154, 0.32)",
              }}
            >
              Use this design →
            </button>
            <span style={{ fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.5 }}>
              No extra generation cost — pick a slot to book this look again.
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * ReuseConfirmationModal - Shows design details and cost transparency before reuse
 */
function ReuseConfirmationModal({ design, onConfirm, onPreview, onCancel }) {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 16,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-elevated)",
          borderRadius: 16,
          maxWidth: 480,
          width: "100%",
          maxHeight: "85vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          border: "1px solid var(--border)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid var(--border)",
            background: "linear-gradient(135deg, rgba(196,135,154,0.08), rgba(196,135,154,0.02))",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
              }}
            >
              💅
            </div>
            <div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700 }}>
                Reuse This Design?
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                Confirm your selection to proceed
              </div>
            </div>
          </div>
        </div>

        {/* Design Preview */}
        <div style={{ padding: 24 }}>
          {designImageUrl(design) && (
            <div style={{ marginBottom: 16, textAlign: "center" }}>
              <img
                src={designImageUrl(design)}
                alt={design.name}
                style={{
                  width: "100%",
                  maxWidth: 280,
                  height: 200,
                  objectFit: "cover",
                  borderRadius: 12,
                  border: "1px solid var(--border)",
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
              {design.name}
            </div>
            {design.tagline && (
              <div style={{ fontSize: 13, fontStyle: "italic", color: "var(--accent)", marginBottom: 8 }}>
                {design.tagline}
              </div>
            )}
            {design.description && (
              <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
                {design.description}
              </div>
            )}
          </div>

          {/* Cost Information */}
          <div
            style={{
              background: "rgba(50,160,80,0.08)",
              border: "1.5px solid rgba(50,160,80,0.2)",
              borderRadius: 12,
              padding: "16px 18px",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "start", gap: 12 }}>
              <div
                style={{
                  fontSize: 24,
                  flexShrink: 0,
                }}
              >
                ✓
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#1F6E36", marginBottom: 4 }}>
                  No Generation Cost
                </div>
                <div style={{ fontSize: 12, color: "#2D5A3B", lineHeight: 1.5 }}>
                  This design was previously generated. Reusing it won't consume your monthly design quota or incur any additional charges.
                </div>
              </div>
            </div>
          </div>

          {/* Design Details */}
          <div
            style={{
              background: "var(--bg-soft)",
              borderRadius: 10,
              padding: 14,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: "var(--text-muted)",
                marginBottom: 10,
              }}
            >
              Design Specifications
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 12 }}>
              {design.nailShape && (
                <div>
                  <span style={{ color: "var(--text-muted)" }}>Shape:</span>{" "}
                  <span style={{ fontWeight: 600 }}>{design.nailShape}</span>
                </div>
              )}
              {design.nailLength && (
                <div>
                  <span style={{ color: "var(--text-muted)" }}>Length:</span>{" "}
                  <span style={{ fontWeight: 600 }}>{design.nailLength}</span>
                </div>
              )}
              {design.finish && (
                <div>
                  <span style={{ color: "var(--text-muted)" }}>Finish:</span>{" "}
                  <span style={{ fontWeight: 600 }}>{design.finish}</span>
                </div>
              )}
              {design.duration && (
                <div>
                  <span style={{ color: "var(--text-muted)" }}>Duration:</span>{" "}
                  <span style={{ fontWeight: 600 }}>{design.duration}</span>
                </div>
              )}
              {design.priceRange && (
                <div>
                  <span style={{ color: "var(--text-muted)" }}>Price:</span>{" "}
                  <span style={{ fontWeight: 600 }}>{design.priceRange}</span>
                </div>
              )}
              {design.difficulty && (
                <div>
                  <span style={{ color: "var(--text-muted)" }}>Difficulty:</span>{" "}
                  <span style={{ fontWeight: 600 }}>{design.difficulty}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={onCancel}
              style={{
                flex: "1 1 120px",
                padding: "12px 20px",
                borderRadius: 999,
                border: "1.5px solid var(--border-strong)",
                background: "var(--bg)",
                color: "var(--text)",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--bg-soft)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--bg)";
              }}
            >
              Cancel
            </button>
            <button
              onClick={onPreview}
              style={{
                flex: "1 1 120px",
                padding: "12px 20px",
                borderRadius: 999,
                border: "1.5px solid var(--border-strong)",
                background: "var(--bg-soft)",
                color: "var(--text)",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Preview
            </button>
            <button
              onClick={onConfirm}
              style={{
                flex: "1 1 140px",
                padding: "12px 20px",
                borderRadius: 999,
                border: "none",
                background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 6px 18px rgba(196, 135, 154, 0.32)",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(196, 135, 154, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 6px 18px rgba(196, 135, 154, 0.32)";
              }}
            >
              Rebook →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase",
          color: "var(--text-muted)", marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: "var(--text)" }}>{children}</div>
    </div>
  );
}

function formatBookingStatus(raw = "") {
  const value = String(raw).toLowerCase();
  if (value === "in_progress") return "In Progress";
  if (value === "no_show" || value === "no-show") return "No Show";
  return String(raw).replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
